import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.warn('No authentication token found. Request will be sent without token.');
    return {
      headers: {
        'Content-Type': 'application/json'
      }
    };
  }
  
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Get all therapist payments (admin only)
export const getAllTherapistPayments = createAsyncThunk(
    "therapistPayments/getAllTherapistPayments",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/therapist-payments`, getAuthConfig());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error occurred while fetching payments" });
        }
    }
);

// Get specific therapist payment record
export const getTherapistPaymentById = createAsyncThunk(
    "therapistPayments/getTherapistPaymentById",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/therapist-payments/${id}`, getAuthConfig());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error occurred while fetching payment record" });
        }
    }
);

// Get logged in therapist's own payment details
export const getTherapistOwnPaymentDetails = createAsyncThunk(
    "therapistPayments/getTherapistOwnPaymentDetails",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/therapist-payments/my-payments`, getAuthConfig());
            
            // Check API response structure
            if (response.data) {
                return response.data;
            } else {
                console.warn('API response data is empty');
                return null;
            }
        } catch (error) {
            // More detailed error information
            const errorData = {
                message: error.response?.data?.message || error.message || "Error occurred while fetching payment details",
                status: error.response?.status,
                statusText: error.response?.statusText,
                url: error.config?.url
            };
            
            // Special handling for 404 - this is expected for new users
            if (error.response?.status === 404) {
                errorData.isNewUser = true;
            }
            
            return rejectWithValue(errorData);
        }
    }
);

// Create new therapist payment record
export const createTherapistPayment = createAsyncThunk(
    "therapistPayments/createTherapistPayment",
    async (paymentData, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_URL}/therapist-payments`, paymentData, getAuthConfig());
            return response.data;
        } catch (error) {
            console.error('Create payment error:', error.response?.data || error.message);
            return rejectWithValue(error.response?.data || { message: "Error occurred while creating payment record" });
        }
    }
);

// Update therapist payment record
export const updateTherapistPayment = createAsyncThunk(
    "therapistPayments/updateTherapistPayment",
    async ({ id, paymentData }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${API_URL}/therapist-payments/${id}`, paymentData, getAuthConfig());
            return response.data;
        } catch (error) {
            console.error('Update payment error:', error.response?.data || error.message);
            return rejectWithValue(error.response?.data || { message: "Error occurred while updating payment record" });
        }
    }
);

// Update therapist's own bank information
export const updateTherapistOwnPaymentInfo = createAsyncThunk(
    "therapistPayments/updateTherapistOwnPaymentInfo",
    async (bankInfo, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${API_URL}/therapist-payments/update-bank-info`, { bankInfo }, getAuthConfig());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error occurred while updating bank information" });
        }
    }
);

// Add earnings to therapist
export const addEarningsToTherapist = createAsyncThunk(
    "therapistPayments/addEarningsToTherapist",
    async ({ therapistId, amount }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_URL}/therapist-payments/add-earnings`, {
                therapistId,
                amount
            }, getAuthConfig());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error occurred while adding earnings" });
        }
    }
);

// Process withdrawal
export const processWithdrawal = createAsyncThunk(
    "therapistPayments/processWithdrawal",
    async ({ therapistId, amount }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_URL}/therapist-payments/process-withdrawal`, {
                therapistId,
                amount
            }, getAuthConfig());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error occurred during withdrawal process" });
        }
    }
);

// Delete therapist payment record
export const deleteTherapistPayment = createAsyncThunk(
    "therapistPayments/deleteTherapistPayment",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`${API_URL}/therapist-payments/${id}`, getAuthConfig());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error occurred while deleting payment record" });
        }
    }
);

// Get specific therapist's payment details (for users)
export const getTherapistPaymentDetails = createAsyncThunk(
    "therapistPayments/getTherapistPaymentDetails",
    async (therapistId, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/therapist-payments/therapist/${therapistId}`, getAuthConfig());
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Error occurred while fetching therapist payment details" });
        }
    }
);

const therapistPaymentsSlice = createSlice({
    name: "therapistPayments",
    initialState: {
        therapistPayments: [],
        currentPayment: null,
        ownPaymentDetails: null,
        loading: false,
        error: null,
        successMessage: null,
        isNewUser: false, // Track if user is new (no payment record exists)
    },
    reducers: {
        clearPaymentError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        resetCurrentPayment: (state) => {
            state.currentPayment = null;
        },
        resetOwnPaymentDetails: (state) => {
            state.ownPaymentDetails = null;
            state.isNewUser = false;
        },
        setIsNewUser: (state, action) => {
            state.isNewUser = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get all therapist payments
            .addCase(getAllTherapistPayments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllTherapistPayments.fulfilled, (state, action) => {
                state.loading = false;
                state.therapistPayments = action.payload;
            })
            .addCase(getAllTherapistPayments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Get therapist payment by ID
            .addCase(getTherapistPaymentById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getTherapistPaymentById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentPayment = action.payload;
            })
            .addCase(getTherapistPaymentById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Get own payment details
            .addCase(getTherapistOwnPaymentDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.isNewUser = false;
            })
            .addCase(getTherapistOwnPaymentDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.ownPaymentDetails = action.payload;
                state.isNewUser = false;
                state.error = null;
            })
            .addCase(getTherapistOwnPaymentDetails.rejected, (state, action) => {
                state.loading = false;
                
                // If it's a 404 error, mark as new user
                if (action.payload?.status === 404 || action.payload?.isNewUser) {
                    state.isNewUser = true;
                    state.error = null; // Don't show error for new users
                } else {
                    state.error = action.payload;
                    state.isNewUser = false;
                }
            })

            // Create therapist payment
            .addCase(createTherapistPayment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createTherapistPayment.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message || "Payment information created successfully";
                state.ownPaymentDetails = action.payload.payment || action.payload;
                state.isNewUser = false;
                
                // If payments list exists, add new record
                if (state.therapistPayments.length > 0) {
                    state.therapistPayments.push(action.payload.payment || action.payload);
                }
            })
            .addCase(createTherapistPayment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Update therapist payment
            .addCase(updateTherapistPayment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateTherapistPayment.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message || "Payment information updated successfully";
                
                // Update current payment and own payment details
                state.currentPayment = action.payload.payment || action.payload;
                state.ownPaymentDetails = action.payload.payment || action.payload;
                
                // Update in list
                const index = state.therapistPayments.findIndex(
                    payment => payment._id === (action.payload.payment?._id || action.payload._id)
                );
                if (index !== -1) {
                    state.therapistPayments[index] = action.payload.payment || action.payload;
                }
            })
            .addCase(updateTherapistPayment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Update own payment info
            .addCase(updateTherapistOwnPaymentInfo.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateTherapistOwnPaymentInfo.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;
                state.ownPaymentDetails = action.payload.payment;
            })
            .addCase(updateTherapistOwnPaymentInfo.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Add earnings
            .addCase(addEarningsToTherapist.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addEarningsToTherapist.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;
                
                // Update in list
                const index = state.therapistPayments.findIndex(
                    payment => payment._id === action.payload.payment._id
                );
                if (index !== -1) {
                    state.therapistPayments[index] = action.payload.payment;
                }
                
                // Update own payment details if it's our record
                if (state.ownPaymentDetails && state.ownPaymentDetails._id === action.payload.payment._id) {
                    state.ownPaymentDetails = action.payload.payment;
                }
            })
            .addCase(addEarningsToTherapist.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Process withdrawal
            .addCase(processWithdrawal.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(processWithdrawal.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;
                
                // Update in list
                const index = state.therapistPayments.findIndex(
                    payment => payment._id === action.payload.payment._id
                );
                if (index !== -1) {
                    state.therapistPayments[index] = action.payload.payment;
                }
                
                // Update own payment details if it's our record
                if (state.ownPaymentDetails && state.ownPaymentDetails._id === action.payload.payment._id) {
                    state.ownPaymentDetails = action.payload.payment;
                }
            })
            .addCase(processWithdrawal.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Delete therapist payment
            .addCase(deleteTherapistPayment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteTherapistPayment.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;
                // Remove deleted record from list
                state.therapistPayments = state.therapistPayments.filter(
                    payment => payment._id !== action.meta.arg
                );
            })
            .addCase(deleteTherapistPayment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { 
    clearPaymentError, 
    clearSuccessMessage, 
    resetCurrentPayment, 
    resetOwnPaymentDetails,
    setIsNewUser
} = therapistPaymentsSlice.actions;

export default therapistPaymentsSlice.reducer;